import { type ReactNode, useEffect } from 'react';
import { useT } from '../i18n';
import { useUi } from '../store/ui';
import { tg } from '../tg/bridge';
import { CloseIcon } from './icons';

/**
 * A dialog over the game (the reference's white panel with a green round close button on its corner). Tapping the
 * dim backdrop or Telegram's Back button closes it.
 */
export function Dialog({
  title,
  children,
  className,
  onClose,
  centered = false,
  testId,
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
  onClose?: () => void;
  centered?: boolean;
  testId?: string;
}) {
  const t = useT();
  const close = onClose ?? useUi.getState().closeDialog;
  useEffect(() => tg.backButton.show(close), [close]);
  return (
    <div
      className={centered ? 'dialog-layer centered' : 'dialog-layer'}
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <div className={`dialog ${className ?? ''}`} role="dialog" aria-modal data-testid={testId}>
        <button
          className="dialog-close"
          onClick={close}
          aria-label={t('common.close')}
          data-testid="dialog-close"
        >
          <CloseIcon />
        </button>
        {title !== undefined && <div className="dialog-title">{title}</div>}
        <div className="dialog-body">{children}</div>
      </div>
    </div>
  );
}

/** A panel over the chat, below the table (gifts for a player, bottles, stickers). */
export function Panel({
  head,
  children,
  onClose,
  testId,
}: {
  head: ReactNode;
  children: ReactNode;
  onClose?: () => void;
  testId?: string;
}) {
  const t = useT();
  const close = onClose ?? useUi.getState().closeSheet;
  useEffect(() => tg.backButton.show(close), [close]);
  return (
    <div className="panel" role="dialog" data-testid={testId}>
      <div className="panel-head">{head}</div>
      <div className="panel-body">{children}</div>
      <button
        className="panel-close"
        onClick={close}
        aria-label={t('common.close')}
        data-testid="panel-close"
      >
        <CloseIcon size={20} />
      </button>
    </div>
  );
}
